import { useMutation } from '@apollo/client/react';
import { Button, Form, Modal } from 'antd';
import { filter, map } from 'lodash';
import React, { useMemo, useState } from 'react';
import {
  GA_EVENT,
  GA_LABEL,
  ROUTES,
  STAGE_STATUS,
  TAB_KEYS,
} from '../../common/constants';
import { Event } from '../../common/trackEvents';
import useRouter from '../../common/useRouter';
import { formValidatorRules, titleCase } from '../../common/utils';
import CommonSelect from '../../components/CommonSelect';
import { UPDATE_REGISTER_LOG_APPROVAL } from './graphql/Mutation';

const { required } = formValidatorRules;

const RegisterApproveModal = (props) => {
  const {
    showModal,
    setShowModal,
    data,
    title,
    status,
    logId,
    commentData,
    additionalComments,
  } = props;
  const { navigate } = useRouter();
  const [form] = Form.useForm();
  const [showApproverModal, setShowApproverModal] = useState(false);
  const [isSelectedAll, setIsSelectedAll] = useState(false);
  const [selectedApprovers, setSelectedApprovers] = useState([]);
  const [approversData, setApproversData] = useState([]);

  const handleCancel = () => {
    setShowModal(false);
  };

  useMemo(() => {
    const filteredData = filter(
      data?.register?.approvers,
      (record) => record?.levelNumber === data?.levelNumber + 1,
    );
    setApproversData(filteredData);
    if (filteredData?.length === 1) {
      const singleApprover = filteredData?.[0];
      const approverId =
        singleApprover?.externalUserApproverId ||
        singleApprover?.internalUserApproverId;

      form.setFieldsValue({
        approvers: [approverId],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.register?.approvers]);

  const [updateApprovalStatus, { loading }] = useMutation(
    UPDATE_REGISTER_LOG_APPROVAL,
    {
      onError() {},
      onCompleted() {
        setShowModal(false);
        Event(GA_EVENT.APPROVE_EQC, {
          label: GA_LABEL.APPROVE_EQC,
          // eslint-disable-next-line no-undef
          pathname: window?.location?.href,
          status,
        });
        navigate(`${ROUTES.TODO}/${TAB_KEYS.REGISTER}`, {
          state: { showThankyouModal: true },
        });
      },
    },
  );

  const updateStatus = () => {
    let notifyUserData = null;

    if (!isSelectedAll && selectedApprovers?.length > 0) {
      notifyUserData = map(selectedApprovers, (approver) =>
        Number(approver?.value),
      );
    }
    if (isSelectedAll) {
      notifyUserData = map(approversData, (approver) =>
        Number(
          approver?.externalUserApproverId || approver?.internalUserApproverId,
        ),
      );
    }

    // Separate comments by type
    const detailCommentsData = filter(
      commentData?.comments,
      (comment) => comment?.fieldType === 'detail',
    );

    const fieldCommentsData = filter(
      commentData?.comments,
      (comment) => comment?.fieldType === 'formField',
    );

    // Format detail comments
    const detailComments =
      detailCommentsData?.length > 0
        ? map(detailCommentsData, (comment) => ({
            registerLogDetailEntryId: comment?.fieldId || null,
            remark: comment?.remark || null,
            assets: comment?.images?.length > 0 ? comment?.images : null,
          }))
        : null;

    // Format field comments
    const fieldComments =
      fieldCommentsData?.length > 0
        ? map(fieldCommentsData, (comment) => ({
            registerLogFormFieldEntryId: comment?.fieldId || null,
            remark: comment?.remark || null,
            assets: comment?.images?.length > 0 ? comment?.images : null,
          }))
        : null;

    // Format additional comments
    const additionalComment =
      additionalComments?.length > 0
        ? map(additionalComments, (comment) => ({
            remark: comment?.remark || null,
            assets: comment?.assets?.length > 0 ? comment?.assets : null,
          }))
        : null;

    // Build comments object according to API structure
    const commentsObject = {};

    if (additionalComment) {
      commentsObject.additionalComment = additionalComment;
    }

    if (detailComments) {
      commentsObject.detailComments = detailComments;
    }

    if (fieldComments) {
      commentsObject.fieldComments = fieldComments;
    }

    // Only add comments to data if there are any comments
    const commentsData =
      Object.keys(commentsObject)?.length > 0
        ? { comments: commentsObject }
        : {};

    updateApprovalStatus({
      variables: {
        registerLogId: logId,
        data: {
          currentApprovalLevel: data?.levelNumber,
          approvalStatus: status,
          notifyUser: notifyUserData,
          ...commentsData,
        },
      },
    });
  };

  return (
    <>
      <Modal
        maskClosable={false}
        centered
        open={showModal}
        closable={false}
        footer={null}
        width={350}
        destroyOnHidden
        wrapClassName="approval-confirm-modal"
      >
        <div className="title">{`${title} REGISTER STAGE`}</div>
        <div className="description mb-16">
          {`Are you sure you want to ${title.toLowerCase()} this Register Stage?`}
        </div>
        <div>
          <Button shape="round" className="mr-10" onClick={handleCancel}>
            No
          </Button>
          <Button
            shape="round"
            htmlType="submit"
            type="primary"
            loading={loading}
            onClick={() => {
              if (
                approversData?.length > 0 &&
                status === STAGE_STATUS.APPROVED
              ) {
                setShowApproverModal(true);
                setShowModal(false);
              } else {
                updateStatus();
              }
            }}
          >
            Yes
          </Button>
        </div>
      </Modal>
      <Modal
        maskClosable={false}
        centered
        open={showApproverModal}
        closable={false}
        footer={null}
        destroyOnHidden
        wrapClassName="approval-selection-modal"
      >
        <Form
          form={form}
          layout="vertical"
          className="stage-config"
          onFinish={updateStatus}
        >
          <Form.Item
            rules={[required]}
            name="approvers"
            label={
              <div className="d-flex justify-between width-percent-100 d-flex-wrap">
                <span>Select approvers you want to notify</span>
                {isSelectedAll && (
                  <span
                    className="text-primary pointer"
                    role="button"
                    tabIndex="0"
                    onClick={() => {
                      setIsSelectedAll(false);
                      form.setFieldsValue({
                        approvers: [],
                      });
                    }}
                  >
                    Deselect All
                  </span>
                )}
              </div>
            }
            normalize={(value) => {
              if (value.includes('all')) {
                setIsSelectedAll(true);
                return ['all'];
              }
              return value;
            }}
          >
            <CommonSelect
              mode="multiple"
              disabled={isSelectedAll}
              className="mr-3"
              placeholder="Select Approvers"
              allowClear
              onChange={(_, record) => {
                setSelectedApprovers(record);
              }}
              optionFilterProp="label"
              options={[
                {
                  key: 'approver-select-all',
                  value: 'all',
                  label: ' Select All',
                },
                ...map(approversData, (record) => {
                  return {
                    key:
                      record?.externalUserApproverId ||
                      record?.internalUserApproverId,
                    value:
                      record?.externalUserApproverId ||
                      record?.internalUserApproverId,
                    label: titleCase(
                      record?.externalUserApprover?.name ||
                        record?.internalUserApprover?.name,
                    ),
                    'data-type': record?.externalUserApproverId
                      ? 'external'
                      : 'internal',
                  };
                }),
              ]}
            />
          </Form.Item>
          <Form.Item>
            <div className="d-flex justify-end">
              <Button
                shape="round"
                className="mr-10"
                onClick={() => {
                  setShowApproverModal(false);
                  setIsSelectedAll(false);
                }}
              >
                Cancel
              </Button>
              <Button
                shape="round"
                htmlType="submit"
                type="primary"
                loading={loading}
              >
                Done
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default RegisterApproveModal;
